import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import {getIssueStateIcon, getPullRequestEntityState, getPullRequestStateIcon} from '../constants/stateIcon'
import {TEST_IDS} from '../constants/test-ids'
import type {
  IssuePullRequestStateIcon$data,
  IssuePullRequestStateIcon$key,
} from './__generated__/IssuePullRequestStateIcon.graphql'
import type {IssuePullRequestStateIconSecondary$key} from './__generated__/IssuePullRequestStateIconSecondary.graphql'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import type {PullRequestRowSecondary$key} from './__generated__/PullRequestRowSecondary.graphql'
import {PullRequestRowSecondaryFragment} from './PullRequestRow'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssuePullRequestStateIconSecondaryFragment = graphql`
  fragment IssuePullRequestStateIconSecondary on PullRequest {
    isInMergeQueue
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const stateIconFragment = graphql`
  fragment IssuePullRequestStateIcon on IssueOrPullRequest {
    __typename
    ... on Issue {
      state
      stateReason
    }
    ... on PullRequest {
      isDraft
      isArchived
      pullRequestState: state
    }
  }
`

type IssuePullRequestStateIconProps = {
  id?: string
  dataKey: IssuePullRequestStateIcon$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
}

export function IssuePullRequestStateIcon({id, dataKey, listItemMetadata}: IssuePullRequestStateIconProps) {
  const data = useFragment(stateIconFragment, dataKey)

  if (data.__typename === 'PullRequest') {
    return <LazyIssuePullRequestStateIcon id={id} data={data} listItemMetadata={listItemMetadata} />
  }

  return <IssuePullRequestStateIconInternal data={data} isInMergeQueue={false} />
}

function IssuePullRequestStateIconInternal({
  data,
  isInMergeQueue,
}: {
  data: IssuePullRequestStateIcon$data
  isInMergeQueue: boolean
}) {
  const defaultState = useMemo(() => getIssueStateIcon(null), [])
  let {icon, color, description} = defaultState

  if (data.__typename === 'PullRequest') {
    const state = getPullRequestEntityState(data.isArchived, data.isDraft, isInMergeQueue, data.pullRequestState)
    const stateData = getPullRequestStateIcon(state)
    icon = stateData.icon
    color = stateData.color
    description = stateData.description
  }

  if (data.__typename === 'Issue') {
    const stateOrReason =
      data.state === 'CLOSED' && (data.stateReason === 'NOT_PLANNED' || data.stateReason === 'DUPLICATE')
        ? data.stateReason
        : data.state

    const state = getIssueStateIcon(stateOrReason)
    icon = state.icon
    color = state.color
    description = state.description
  }

  return (
    <ListItemLeadingVisual
      icon={icon}
      color={color}
      description={description}
      data-testid={TEST_IDS.listRowStateIcon}
    />
  )
}

function LazyIssuePullRequestStateIcon({
  data,
  listItemMetadata,
  id,
}: {
  id?: string
  data: IssuePullRequestStateIcon$data
  listItemMetadata?: IssueRowSecondaryQuery$data | null
}) {
  // No real need to check for __typename here, but the linter isnt able to detect that
  if (data.__typename !== 'PullRequest' || !listItemMetadata) {
    return <IssuePullRequestStateIconInternal data={data} isInMergeQueue={false} />
  }

  const prNode = listItemMetadata.nodes?.find(node => node?.id === id)

  return <LazyIssuePullRequestStateIconInternal data={data} secondaryDataKey={prNode} />
}

function LazyIssuePullRequestStateIconInternal({
  data,
  secondaryDataKey,
}: {
  data: IssuePullRequestStateIcon$data
  secondaryDataKey?: PullRequestRowSecondary$key | null
}) {
  const secondaryData = useFragment(PullRequestRowSecondaryFragment, secondaryDataKey)
  const isInMergeQueueData = useFragment<IssuePullRequestStateIconSecondary$key>(
    IssuePullRequestStateIconSecondaryFragment,
    secondaryData,
  )

  return <IssuePullRequestStateIconInternal data={data} isInMergeQueue={!!isInMergeQueueData?.isInMergeQueue} />
}
